import { api } from "@/lib/api";

/**
 * The personal activity screen's own data layer.
 *
 * ⚠️ Kept apart from `analytics.ts` deliberately. That module serves a screen behind
 * `report.read`, scoped by the reader's permissions and elevated to every classification
 * (D-65); this one serves every employee at ~29 sites and is pinned server-side to requests
 * they raised or were invited onto. The two answer different questions about different sets,
 * and a shared type would be the first step towards a shared query.
 */

/**
 * The ranges the screen offers, mirroring `MyActivityEndpoints.AllowedRangeDays`.
 *
 * ⚠️ Longer than the analytics screen's, because a desk measures itself over weeks and a
 * person who raises six requests a year has no chart at all at thirty days.
 *
 * ⚠️ The server REFUSES an unlisted range rather than clamping it, so a mangled link has to
 * resolve to the default here rather than travel and come back a 400 (D-52's rule).
 */
export const MY_ACTIVITY_RANGES = [30, 90, 180, 365] as const;

export type MyActivityRangeDays = (typeof MY_ACTIVITY_RANGES)[number];

export const DEFAULT_MY_ACTIVITY_RANGE: MyActivityRangeDays = 90;

/**
 * Which of the reader's own requests the figures cover.
 *
 * ⚠️ A relationship the reader has, never an id. There is no form of this that can be asked
 * about a colleague — the server resolves every one of them from the session — which is why
 * this is three words rather than a user id in a URL.
 */
export const MY_ACTIVITY_RELATIONSHIPS = ["Mine", "Shared", "MineOrShared"] as const;

export type MyActivitySet = (typeof MY_ACTIVITY_RELATIONSHIPS)[number];

/**
 * Every relationship the server's pin can express, including the one this screen's selector
 * deliberately does not offer.
 *
 * ⚠️ `Assigned` is the WORK section's pin, not a set the requests half can be pointed at:
 * those figures are a raised-cohort (D-53), and a raised-cohort over requests assigned to
 * somebody answers "what did the people I help ask for" under headings written for "what have I
 * asked for". `MyActivityEndpoints.RequestRelationships` refuses it, and
 * `MyActivityContractTests` compares both lists in both directions.
 *
 * ⚠️ It is in this type because the QUEUE accepts all four — the work section's tiles link
 * with `?relationship=Assigned`, which is the same pin the figures were counted under (D-70).
 */
export type MyActivityRelationship = MyActivitySet | "Assigned";

export const DEFAULT_MY_ACTIVITY_RELATIONSHIP: MyActivitySet = "MineOrShared";

/**
 * How each set reads on screen, and on the queue's chip when one arrives as a pin.
 *
 * ⚠️ Covers all four rather than only the three the selector offers, because the queue
 * chips whichever pin it was sent — and a pin with no label would leave the queue narrowed
 * with nothing on screen naming the filter, which is what the chips exist to prevent (D-110).
 */
export const relationshipLabel: Record<MyActivityRelationship, string> = {
  Mine: "Raised by me",
  Shared: "Shared with me",
  MineOrShared: "Both",
  Assigned: "Assigned to me",
};

/**
 * What each set actually covers, in a sentence.
 *
 * ⚠️ Said out loud because "Both" is the default, and on it the period's figure counts
 * requests a colleague raised. A heading reading "you raised 40" over a number holding twelve
 * of somebody else's is the claim-versus-reality defect this product keeps finding (D-110,
 * D-137) — the number is right and the sentence above it is not.
 */
export const relationshipBlurb: Record<MyActivitySet, string> = {
  Mine: "Every figure covers requests you raised, including any the service desk raised for you.",
  Shared: "Every figure covers requests somebody else raised and invited you onto.",
  MineOrShared:
    "Every figure covers requests you raised and requests somebody invited you onto — so a " +
    "count here can include a colleague's request.",
};

export interface MyActivityRange {
  days: number;
  startUtc: string;
  endUtc: string;
  previousStartUtc: string;
  timeZoneId: string;
}

/**
 * The point-in-time tiles.
 *
 * ⚠️ These describe RIGHT NOW and ignore the range, and the screen says so — a figure that
 * silently answers a different question from the controls above it is how somebody comes to
 * quote it for the wrong period.
 */
export interface MyActivityNow {
  open: number;

  /**
   * ⚠️ Pinned server-side to requests the reader RAISED, whatever set is selected. Nobody is
   * waiting on a participant, and telling somebody they are holding up work they cannot
   * answer for is worse than not telling them at all.
   */
  awaitingYourReply: number;

  /** Null when the desk is not asking for ratings — never zero (D-63). */
  awaitingYourRating: number | null;
}

export interface MyActivityPeriod {
  /**
   * ⚠️ "Raised" is when a request was created, not by whom. On `MineOrShared` this includes
   * requests a colleague raised — see {@link relationshipBlurb}.
   */
  raised: number;
  resolved: number;
  stillOpen: number;
}

export interface MyActivityDay {
  /** A calendar date already resolved to the reader's timezone by the server. */
  day: string;
  raised: number;
}

export interface MyActivityStatusRow {
  /** The server enum's name, labelled in the browser so a requester reads their own words. */
  status: string;
  count: number;
}

export interface MyActivityCategoryRow {
  /** Null on the folded "Other" row, which names no single category. */
  categoryId: string | null;
  label: string;
  count: number;
}

export interface MyActivityDurationRow {
  label: string;
  count: number;
}

export interface MyActivityResolution {
  /** The denominator, always on screen (D-53). */
  resolved: number;

  /** Raised in the period and not finished — excluded from the bands rather than counted slow. */
  undetermined: number;

  /** Null when nothing in the cohort has been resolved. Never zero. */
  medianHours: number | null;

  bands: MyActivityDurationRow[];
}

/** Everything the requests half of the page draws. Everybody has this. */
export interface MyActivityRequests {
  now: MyActivityNow;
  period: MyActivityPeriod;
  volume: MyActivityDay[];
  byStatus: MyActivityStatusRow[];
  byCategory: MyActivityCategoryRow[];
  resolution: MyActivityResolution;
}

/**
 * What is on the reader's plate right now. These ignore the range, and the screen says so.
 *
 * ⚠️ All four have a queue filter behind them, so every tile opens exactly the rows it
 * counted (D-70) — `AttentionFilter.Breaching` was added for `pastDue`, because on a work
 * screen the number somebody most wants to press is the one saying something is late.
 */
export interface MyActivityWorkNow {
  open: number;
  needsYourReply: number;
  pastDue: number;
  goneQuiet: number;
}

export interface MyActivityWorkThroughput {
  resolved: number;

  /**
   * The same duration immediately before this one.
   *
   * ⚠️ A COUNT, never a direction and never a colour. A rise in resolutions is not
   * obviously good — it can be a week of easy requests — and colouring it would be the
   * verdict D-55 refused, printed on a screen about the person reading it.
   */
  resolvedPrevious: number;
}

export interface MyActivityWorkDay {
  /** A calendar date already resolved to the reader's timezone by the server. */
  day: string;
  resolved: number;
}

export interface MyActivityWorkSpeed {
  medianFirstReplyMinutes: number | null;

  /** Its OWN denominator, deliberately not shared with the resolution one (D-53). */
  firstReplyMeasured: number;

  medianResolutionHours: number | null;
  resolutionMeasured: number;
  bands: MyActivityDurationRow[];
}

export interface MyActivityRatio {
  numerator: number;
  denominator: number;

  /** Null when the denominator is zero — the server never divides by nothing. */
  percent: number | null;
}

export interface MyActivityWorkQuality {
  onTime: MyActivityRatio;
  fixedFirstTime: MyActivityRatio;
  reopened: MyActivityRatio;

  /**
   * ⚠️ MARKED, never withheld (D-130's amendment to D-64). The reader can count their own
   * five requests, so a "—" where they know the answer reads as the product hiding something
   * from them about themselves.
   */
  smallSample: boolean;
}

export interface MyActivityWorkEffort {
  /** A floor on effort, never a measure of it (D-56). Nobody is required to run a timer. */
  loggedMinutes: number;
  sessions: number;

  /** Timers nobody stopped, closed by the cap. Shown, because a total resting on them is one
   * to distrust and only the system can say so. */
  autoClosedSessions: number;

  /** Null unless there is both recorded time and something resolved to divide it by. */
  averageMinutesPerResolved: number | null;
}

export interface MyActivityStarRow {
  stars: number;
  count: number;
}

export interface MyActivityWorkSatisfaction {
  /** Five counts, never a sixth number on its own (D-130). */
  stars: MyActivityStarRow[];
  total: number;

  /** Null when nobody has rated anything — never 0 (D-53). */
  average: number | null;
  smallSample: boolean;
}

export interface MyActivityWorkScope {
  /** The reader's own `ticket.read` scope, as its name. Rendering only. */
  coverage: string;

  /** Teams the reader's `ticket.read` REACHES, not teams they belong to (D-116). */
  teams: string[];

  /** NAMED, never counted (D-52). */
  classifications: string[];
}

/** Everything the work half draws. Absent for anybody who does not work requests. */
export interface MyActivityWork {
  now: MyActivityWorkNow;
  throughput: MyActivityWorkThroughput;
  resolved: MyActivityWorkDay[];
  byCategory: MyActivityCategoryRow[];
  speed: MyActivityWorkSpeed;
  quality: MyActivityWorkQuality;
  effort: MyActivityWorkEffort;
  satisfaction: MyActivityWorkSatisfaction;
  scope: MyActivityWorkScope;
}

/**
 * One page, both halves.
 *
 * ⚠️ <b>THE TWO HALVES COUNT IN DIFFERENT FRAMES, and on one page that matters MORE rather
 * than less.</b> `requests` is a cohort of what was RAISED in the period (D-53); `work` is what
 * was RESOLVED in it (D-55). Two frames a scroll apart will be compared, found inconsistent, and
 * one of them assumed broken — so every card on both halves names its own frame on screen.
 */
export interface MyActivity {
  range: MyActivityRange;
  relationship: MyActivitySet;

  /** What this person has asked the desk for. Everybody has this. */
  requests: MyActivityRequests;

  /**
   * What this person's own service desk work looks like, or null for anybody not on the desk.
   *
   * ⚠️ <b>Its nullness is the only signal, and there is deliberately no companion flag.</b>
   * The section is withheld from the WIRE rather than hidden here (D-82), and a second boolean
   * saying whether it should be drawn could only ever come to disagree with the data beside it —
   * the shape D-122 and D-136 both found in production.
   */
  work: MyActivityWork | null;

  computedAtUtc: string;
}

export function fetchMyActivity(
  query: { days: MyActivityRangeDays; relationship: MyActivitySet },
  signal?: AbortSignal,
): Promise<MyActivity> {
  const params = new URLSearchParams({
    days: String(query.days),
    relationship: query.relationship,
  });

  return api<MyActivity>(`/api/my-activity?${params.toString()}`, { signal });
}

/**
 * The days a chart draws, with the quiet ones filled in.
 *
 * ⚠️ Generic over the count's NAME so both lenses share one implementation. The requests
 * half counts what was raised on a day and the work half what was resolved on it; two copies of
 * the walk is how one of them comes to draw a fortnight of silence as a straight line between
 * two busy weeks while the other does not.
 *
 * ⚠️ <b>The server sends only days that carry a request, and an absent day is a ZERO rather
 * than a gap.</b> That is knowable here and nowhere else in this product: the payload is the
 * complete set of requests the reader raised in the window, so a silent day is a day on which
 * they raised none. D-53's "a day with no determined outcome is a gap, not a zero" is about a
 * figure that could not be COMPUTED, which is a different thing.
 *
 * ⚠️ Filled rather than skipped, because a series that omits its empty days draws a quiet
 * fortnight as a straight line between two busy ones — `VolumeChart` says the same about the
 * analytics screen's series.
 *
 * ⚠️ Days are walked as plain calendar dates and never through a `Date` with a timezone. The
 * server already resolved them to the reader's zone; re-reading them as instants would shift
 * the whole series by a day for anybody west of UTC.
 */
export function fillQuietDays<K extends string>(
  volume: readonly ({ day: string } & Record<K, number>)[],
  range: { startUtc: string; endUtc: string },
  key: K,
): ({ day: string } & Record<K, number>)[] {
  const raisedOn = new Map(volume.map((point) => [point.day, point[key]]));

  const first = dayNumber(range.startUtc.slice(0, 10));
  const last = dayNumber(range.endUtc.slice(0, 10));
  if (first === null || last === null || last < first) return [...volume];

  // A year of daily points is the longest range on offer and is drawn on one chart; anything
  // beyond that is a mangled range rather than a reader's choice, and is left alone.
  if (last - first > 400) return [...volume];

  const days: ({ day: string } & Record<K, number>)[] = [];
  for (let at = first; at <= last; at++) {
    const day = dayString(at);

    days.push({ day, [key]: raisedOn.get(day) ?? 0 } as { day: string } & Record<K, number>);
  }

  return days;
}

/** A `YYYY-MM-DD` as a count of days, or null if it is not one. */
function dayNumber(day: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;

  const at = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(at) ? Math.floor(at / 86_400_000) : null;
}

function dayString(dayNumber: number): string {
  return new Date(dayNumber * 86_400_000).toISOString().slice(0, 10);
}

/**
 * A whole number of MINUTES as somebody would say it.
 *
 * ⚠️ Never "0m" where nothing was recorded — the caller distinguishes those cases, and
 * D-56 is explicit that no timer reads as "not logged" rather than as zero effort.
 */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours < 10 && rest > 0) return `${hours}h ${rest}m`;
  return `${hours}h`;
}

/** A whole number of hours as somebody would say it. */
export function formatHours(hours: number | null): string {
  if (hours === null) return "—";
  if (hours < 1) return "under an hour";
  if (hours < 48) return `${hours} ${hours === 1 ? "hour" : "hours"}`;

  const days = Math.round(hours / 24);
  return `${days} days`;
}
